// Iter 91ac — Settings → Dashboard panel that lets a super_admin remap the
// 4 Status Bar Pingdom banners to any check discovered on the account.
// Reads/writes the singleton mapping via /pingdom/{checks,banners}.
import { useEffect, useState } from "react";
import { Radio, Save, RefreshCw, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export function PingdomBannerMapping({ canManage = true }) {
  const [checks, setChecks] = useState([]);
  const [banners, setBanners] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const load = () => {
    setLoadErr(null);
    Promise.all([
      api.get("/pingdom/checks").then((r) => r.data).catch((e) => ({ error: e })),
      api.get("/pingdom/banners").then((r) => r.data).catch((e) => ({ error: e })),
    ]).then(([c, b]) => {
      if (c.error || b.error) {
        setLoadErr("Failed to load Pingdom data — check the PINGDOM_API_TOKEN.");
      } else if (c.error === undefined && c.checks && c.checks.length === 0 && c.error === undefined) {
        setLoadErr("Pingdom API returned no checks — token may be invalid.");
      }
      setChecks(c.checks || []);
      setBanners(b.banners || []);
    });
  };
  useEffect(() => { load(); }, []);

  if (!banners) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500"
           data-testid="pingdom-banner-mapping-loading">
        Loading Pingdom mapping…
      </div>
    );
  }

  const setRow = (i, patch) =>
    setBanners((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/pingdom/banners", { banners });
      setBanners(data.banners);
      toast.success("Pingdom banner mapping saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="pingdom-banner-mapping">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-primary" />
          <Label className="text-sm font-semibold text-slate-700">Pingdom Banner Mapping</Label>
          <span className="text-[11px] font-normal text-slate-400">
            — assign each Status Bar tile to a Pingdom check
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={load} title="Refresh check list"
                data-testid="pingdom-banner-mapping-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {loadErr && (
        <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
             data-testid="pingdom-banner-mapping-error">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          {loadErr}
        </div>
      )}

      <div className="space-y-2">
        {banners.map((b, i) => (
          <div key={i}
               className="grid gap-2 rounded-lg border border-slate-200 bg-white p-2 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_minmax(0,1.6fr)]"
               data-testid={`pingdom-banner-row-${i}`}>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-400">Label</Label>
              <Input value={b.label} disabled={!canManage}
                     onChange={(e) => setRow(i, { label: e.target.value })}
                     data-testid={`pingdom-banner-label-${i}`}
                     className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-400">Pingdom check</Label>
              <Select value={String(b.check_id)} disabled={!canManage}
                      onValueChange={(v) => setRow(i, { check_id: parseInt(v, 10) })}>
                <SelectTrigger className="h-8 text-sm"
                               data-testid={`pingdom-banner-check-${i}`}>
                  <SelectValue placeholder="Choose a check…" />
                </SelectTrigger>
                <SelectContent>
                  {checks.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      #{c.id} · {c.name}{c.hostname ? ` (${c.hostname})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-400">
                Banner image URL <span className="text-slate-300">(optional)</span>
              </Label>
              <Input value={b.url || ""} disabled={!canManage}
                     onChange={(e) => setRow(i, { url: e.target.value })}
                     data-testid={`pingdom-banner-url-${i}`}
                     placeholder="https://share.pingdom.com/banners/…"
                     className="h-8 text-sm" />
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving || !canManage}
                data-testid="pingdom-banner-mapping-save">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save mapping"}
        </Button>
      </div>
    </div>
  );
}

export default PingdomBannerMapping;
